import React, { useState } from 'react';
import { SearchIcon, PlusIcon, XIcon, MapPinIcon, CheckIcon } from 'lucide-react';
interface Address {
  id: string;
  line1: string;
  city: string;
  country: string;
  addressType: string;
  isPrimary: boolean;
  associations: Array<{
    type: string;
    name: string;
    id: string;
  }>;
}
interface AddressSelectorProps {
  selectedAddressIds: string[];
  onAddressesChange: (ids: string[]) => void;
  availableAddresses: Address[];
  onAddAddress: (address: Omit<Address, 'id' | 'associations'>) => void;
}
export function AddressSelector({
  selectedAddressIds,
  onAddressesChange,
  availableAddresses,
  onAddAddress
}: AddressSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  // New address form state
  const [newAddress, setNewAddress] = useState({
    line1: '',
    city: '',
    country: 'United States',
    addressType: 'Physical',
    isPrimary: false
  });
  const selectedAddresses = availableAddresses.filter(addr => selectedAddressIds.includes(addr.id));
  const filteredAddresses = availableAddresses.filter(addr => {
    const matchesSearch = addr.line1.toLowerCase().includes(searchTerm.toLowerCase()) || addr.city.toLowerCase().includes(searchTerm.toLowerCase());
    const notAlreadySelected = !selectedAddressIds.includes(addr.id);
    return matchesSearch && notAlreadySelected;
  });
  const handleAddClick = (addressId: string) => {
    onAddressesChange([...selectedAddressIds, addressId]);
    setSearchTerm('');
    setShowDropdown(false);
  };
  const handleRemoveClick = (addressId: string) => {
    onAddressesChange(selectedAddressIds.filter(id => id !== addressId));
  };
  const handleCreateAddress = () => {
    if (!newAddress.line1 || !newAddress.city) return;
    onAddAddress(newAddress);
    setNewAddress({
      line1: '',
      city: '',
      country: 'United States',
      addressType: 'Physical',
      isPrimary: false
    });
    setShowAddForm(false);
  };
  return <div className="space-y-4" data-id="element-8190">
      {/* Header with Add Button */}
      <div className="flex justify-between items-start" data-id="element-8191">
        <div data-id="element-8192">
          <h4 className="text-sm font-medium text-gray-700" data-id="element-8193">
            {selectedAddresses.length > 0 ? 'Associated Addresses' : 'Add Address'}
          </h4>
          {selectedAddresses.length === 0 && <p className="text-sm text-gray-500 mt-1" data-id="element-8194">
              No addresses associated yet
            </p>}
        </div>
        {!showAddForm && <button type="button" onClick={() => setShowAddForm(true)} className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8195">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-8196" />
            New
          </button>}
      </div>

      {/* Selected Addresses List */}
      {selectedAddresses.length > 0 && <div className="space-y-3" data-id="element-8197">
          {selectedAddresses.map(address => <div key={address.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-8198">
              <div className="flex items-start space-x-3" data-id="element-8199">
                <div className="flex-shrink-0" data-id="element-8200">
                  <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-8201">
                    <MapPinIcon className="h-5 w-5 text-indigo-600" data-id="element-8202" />
                  </div>
                </div>
                <div className="flex-1 min-w-0" data-id="element-8203">
                  <div className="flex items-center" data-id="element-8204">
                    <p className="text-sm font-medium text-gray-900" data-id="element-8205">
                      {address.addressType}
                    </p>
                    {address.isPrimary && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-8206">
                        Primary
                      </span>}
                  </div>
                  <p className="text-sm text-gray-500" data-id="element-8207">{address.line1}</p>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-8208">
                    {address.city}, {address.country}
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => handleRemoveClick(address.id)} className="ml-4 text-gray-400 hover:text-red-600" data-id="element-8209">
                <XIcon className="h-5 w-5" data-id="element-8210" />
              </button>
            </div>)}
        </div>}

      {/* Add Form */}
      {showAddForm && <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-8211">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-8212">
            New Address
          </h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-8213">
            <div className="sm:col-span-2" data-id="element-8214">
              <label className="block text-xs font-medium text-gray-700" data-id="element-8215">
                Address Line 1
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={newAddress.line1} onChange={e => setNewAddress({
            ...newAddress,
            line1: e.target.value
          })} data-id="element-8216" />
            </div>
            <div data-id="element-8217">
              <label className="block text-xs font-medium text-gray-700" data-id="element-8218">
                City
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={newAddress.city} onChange={e => setNewAddress({
            ...newAddress,
            city: e.target.value
          })} data-id="element-8219" />
            </div>
            <div data-id="element-8220">
              <label className="block text-xs font-medium text-gray-700" data-id="element-8221">
                Country
              </label>
              <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={newAddress.country} onChange={e => setNewAddress({
            ...newAddress,
            country: e.target.value
          })} data-id="element-8222">
                <option value="United States" data-id="element-8223">United States</option>
                <option value="United Kingdom" data-id="element-8224">United Kingdom</option>
                <option value="Australia" data-id="element-8225">Australia</option>
                <option value="Canada" data-id="element-8226">Canada</option>
              </select>
            </div>
            <div data-id="element-8227">
              <label className="block text-xs font-medium text-gray-700" data-id="element-8228">
                Type
              </label>
              <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={newAddress.addressType} onChange={e => setNewAddress({
            ...newAddress,
            addressType: e.target.value
          })} data-id="element-8229">
                <option value="Physical" data-id="element-8230">Physical</option>
                <option value="Billing" data-id="element-8231">Billing</option>
                <option value="Shipping" data-id="element-8232">Shipping</option>
                <option value="Registered" data-id="element-8233">Registered</option>
              </select>
            </div>
            <div className="flex items-center pt-5" data-id="element-8234">
              <input id="isPrimaryAddr" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" checked={newAddress.isPrimary} onChange={e => setNewAddress({
            ...newAddress,
            isPrimary: e.target.checked
          })} data-id="element-8235" />
              <label htmlFor="isPrimaryAddr" className="ml-2 block text-sm text-gray-900" data-id="element-8236">
                Set as primary
              </label>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2" data-id="element-8237">
            <button type="button" onClick={() => setShowAddForm(false)} className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-8238">
              Cancel
            </button>
            <button type="button" onClick={handleCreateAddress} className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-8239">
              Add Address
            </button>
          </div>
        </div>}
    </div>;
}